import { LitElement, html, css } from 'lit';
import { base } from '../styles.js';

/**
 * How long a toast dwells before it starts leaving, by type. An info or success toast acknowledges
 * something the officer just did and can already see on the screen behind it, so it can be brief.
 * A warn or danger toast is often the ONLY place a fact is stated at all -- placeTimedOut's
 * possible duplicate transaction, statusUnavailable's stopped polling -- and 3.6 s is not long
 * enough to read a two-sentence warning and act on it. Those get more than twice as long.
 */
export const SHOW_MS = { info: 3600, success: 3600, warn: 8000, danger: 8000 };
const EXIT_MS = 250;
const GLYPH = { success: '✓', info: 'i', warn: '!', danger: '!' };

/**
 * Top-centre toasts that announce themselves and leave on their own. Fixed to the viewport, so
 * they must sit outside any animated (transformed) screen container -- the app places the stack
 * beside <main>, not inside it.
 */
export class ToastStack extends LitElement {
  static properties = { toasts: { state: true } };

  static styles = [base, css`
    .toast-stack {
      position: fixed; top: 18px; left: 50%; transform: translateX(-50%); z-index: 200;
      display: flex; flex-direction: column; gap: 10px; align-items: center; pointer-events: none;
      max-width: calc(100vw - 24px);
    }
    .toast {
      pointer-events: auto; min-width: 260px; max-width: 380px; display: flex; align-items: flex-start; gap: 11px;
      background: var(--pbg-surface); border: 1px solid var(--pbg-gray-200); border-left-width: 4px;
      border-radius: var(--pbg-radius); box-shadow: var(--pbg-shadow-lg); padding: 12px 14px; font-size: 13px;
      animation: toastIn .28s cubic-bezier(.2, .7, .2, 1);
    }
    .toast.out { animation: toastOut .25s ease forwards; }
    @keyframes toastIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: none; } }
    @keyframes toastOut { to { opacity: 0; transform: translateY(-8px); } }
    @media (prefers-reduced-motion: reduce) { .toast { animation: none; } .toast.out { animation: none; opacity: 0; } }
    @media (max-width: 480px) { .toast { min-width: 0; width: 100%; } }
    .ti {
      flex: 0 0 auto; width: 20px; height: 20px; border-radius: 50%; color: #fff; display: flex;
      align-items: center; justify-content: center; font-size: 12px; font-weight: 800;
    }
    .tt { font-weight: 600; color: var(--pbg-ink); }
    .td { color: var(--pbg-gray-700); margin-top: 2px; font-size: 12px; }
    .toast.success { border-left-color: var(--pbg-success); } .toast.success .ti { background: var(--pbg-success); }
    .toast.info { border-left-color: var(--pbg-blue); } .toast.info .ti { background: var(--pbg-blue); }
    .toast.warn { border-left-color: var(--pbg-warning); } .toast.warn .ti { background: var(--pbg-warning); }
    .toast.danger { border-left-color: var(--pbg-danger); } .toast.danger .ti { background: var(--pbg-danger); }
  `];

  #seq = 0;

  constructor() {
    super();
    this.toasts = [];
  }

  /** @param {{type?: 'success'|'info'|'warn'|'danger', title: string, desc?: string}} toast */
  push({ type = 'info', title, desc }) {
    const id = ++this.#seq;
    this.toasts = [...this.toasts, { id, type, title, desc, out: false }];
    setTimeout(() => {
      this.toasts = this.toasts.map((t) => (t.id === id ? { ...t, out: true } : t));
      setTimeout(() => { this.toasts = this.toasts.filter((t) => t.id !== id); }, EXIT_MS);
    }, SHOW_MS[type] ?? SHOW_MS.info);
  }

  render() {
    return html`
      <div class="toast-stack" aria-live="assertive" role="status">
        ${this.toasts.map((t) => html`
          <div class="toast ${t.type} ${t.out ? 'out' : ''}">
            <span class="ti" aria-hidden="true">${GLYPH[t.type] ?? 'i'}</span>
            <div class="tbody">
              <div class="tt">${t.title}</div>
              ${t.desc ? html`<div class="td">${t.desc}</div>` : ''}
            </div>
          </div>`)}
      </div>`;
  }
}

customElements.define('epc-toast-stack', ToastStack);
